import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Box, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Button, Grid,
    MenuItem, Chip, Alert, CircularProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, TablePagination, Card, CardContent,
    Accordion, AccordionSummary, AccordionDetails, Collapse, Autocomplete, InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    AccountBalance as AccountBalanceIcon,
    Receipt as ReceiptIcon,
    Assessment as AssessmentIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    CheckCircle as CheckCircleIcon,
    ExpandMore as ExpandMoreIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { isAdmin, hasPermission, hasFluxoCaixaAccess } from '../utils/auth';

function FluxoCaixa() {
    const navigate = useNavigate();
    const [lancamentos, setLancamentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasAccess, setHasAccess] = useState(false);
    const [canViewStats, setCanViewStats] = useState(false);
    const [canViewReports, setCanViewReports] = useState(false);
    const [canViewBasic, setCanViewBasic] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);
    const [categorias, setCategorias] = useState([]);
    const [categoriasAgrupadas, setCategoriasAgrupadas] = useState({ RECEITA: {}, DESPESA: {} });
    const [estatisticas, setEstatisticas] = useState({
        total_receitas: 0,
        total_despesas: 0,
        saldo_liquido: 0,
        total_lancamentos: 0,
        total_receber: 0,
        total_pagar: 0
    });
    const [dividas, setDividas] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [dividaDialogOpen, setDividaDialogOpen] = useState(false);
    const [baixaDialogOpen, setBaixaDialogOpen] = useState(false);
    const [dividaSelecionada, setDividaSelecionada] = useState(null);
    const [expandedDividas, setExpandedDividas] = useState({});
    const [resumoMensal, setResumoMensal] = useState([]);
    const [contasReceber, setContasReceber] = useState([]);
    const [contasPagar, setContasPagar] = useState([]);
    const [fluxoMensal, setFluxoMensal] = useState([]);
    const [mesSelecionado, setMesSelecionado] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
    const [anoRelatorio, setAnoRelatorio] = useState(new Date().getFullYear().toString());
    const [clientes, setClientes] = useState([]);
    const [processos, setProcessos] = useState([]);
    const [filtros, setFiltros] = useState({
        data_inicio: '',
        data_fim: '',
        tipo: '',
        categoria: ''
    });
    const [filtroClienteDivida, setFiltroClienteDivida] = useState('');
    const [filtroStatusDivida, setFiltroStatusDivida] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        data: (() => {
            const hoje = new Date();
            const ano = hoje.getFullYear();
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const dia = String(hoje.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        })(),
        tipo: '',
        categoria: '',
        descricao: '',
        valor: '',
        observacoes: '',
        divida_id: null,
        cliente_id: null,
        processo_id: null
    });

    // Verifica permissões ao montar o componente
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            
            // Verifica se tem acesso ao Fluxo de Caixa
            const access = await hasFluxoCaixaAccess();
            if (!access) {
                setError('Você não tem permissão para acessar o Fluxo de Caixa.');
                setCheckingPermissions(false);
                // Redireciona após 2 segundos
                setTimeout(() => {
                    navigate('/');
                }, 2000);
                return;
            }
            setHasAccess(true);

            // Se for admin, tem acesso total (não precisa verificar permissões)
            if (isAdmin()) {
                setCanViewStats(true);
                setCanViewReports(true);
                setCanViewBasic(true);
                setActiveTab(0); // Admin começa na primeira tab
                setCheckingPermissions(false);
                return;
            }

            // Verifica permissões granulares (apenas para não-admin)
            // Para statistics: requer permissão específica 'statistics' (não basta apenas 'read' ou 'view')
            const hasStatsPerm = await hasPermission('fluxo_caixa', 'statistics');
            
            // Para reports: requer permissão específica 'reports' (não basta apenas 'read' ou 'view')
            const hasReportsPerm = await hasPermission('fluxo_caixa', 'reports');
            
            // Para básico: verifica 'view' ou 'read' (qualquer permissão relacionada permite lançamentos e dívidas)
            const hasReadPerm = await hasPermission('fluxo_caixa', 'read');
            const hasViewPerm = await hasPermission('fluxo_caixa', 'view');
            const hasAnyPerm = hasReadPerm || hasViewPerm || hasStatsPerm || hasReportsPerm;
            const basic = hasAnyPerm; // Básico requer qualquer permissão relacionada
            
            setCanViewStats(hasStatsPerm); // Apenas permissão específica 'statistics'
            setCanViewReports(hasReportsPerm); // Apenas permissão específica 'reports'
            setCanViewBasic(basic); // Qualquer permissão relacionada
            
            // Ajusta activeTab para primeira tab disponível se necessário
            if (!basic && !hasReportsPerm) {
                setActiveTab(0);
            } else if (!basic && hasReportsPerm) {
                // Se não tem acesso básico, mas tem reports, mostra apenas reports (activeTab será 2 ou 3, mas tabs renderizadas serão 0 e 1)
                setActiveTab(2); // Começa no Relatório Geral
            } else if (basic && !hasReportsPerm) {
                // Se tem acesso básico mas não reports, mostra apenas básico (tabs 0 e 1)
                setActiveTab(0);
            } else {
                // Tem ambos, começa em 0
                setActiveTab(0);
            }
            
            setCheckingPermissions(false);
        };

        checkPermissions();
    }, [navigate]);

    // Função helper para calcular o valor do Tabs baseado no activeTab e permissões
    const getTabValue = () => {
        if (!canViewBasic && canViewReports) {
            // Apenas reports disponível: activeTab 2 -> tab index 0, activeTab 3 -> tab index 1
            return activeTab === 2 ? 0 : activeTab === 3 ? 1 : 0;
        } else if (canViewBasic && !canViewReports) {
            // Apenas básico disponível: activeTab 0 -> tab index 0, activeTab 1 -> tab index 1
            return activeTab <= 1 ? activeTab : 0;
        } else {
            // Ambos disponíveis: índices diretos
            return activeTab;
        }
    };

    // Função helper para mapear índice do Tabs para activeTab real
    const mapTabIndexToActiveTab = (tabIndex) => {
        if (!canViewBasic && canViewReports) {
            // Tabs renderizadas: 0=Relatório, 1=Fluxo -> activeTab: 2, 3
            return tabIndex === 0 ? 2 : 3;
        } else if (canViewBasic && !canViewReports) {
            // Tabs renderizadas: 0=Lançamentos, 1=Dívidas -> activeTab: 0, 1
            return tabIndex;
        } else {
            // Ambos: índices diretos
            return tabIndex;
        }
    };

    useEffect(() => {
        const hoje = new Date();
        const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        // Usa data local (não UTC) para evitar problema de timezone
        const formatarDataLocal = (data) => {
            const ano = data.getFullYear();
            const mes = String(data.getMonth() + 1).padStart(2, '0');
            const dia = String(data.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        };
        setFiltros({
            ...filtros,
            data_inicio: formatarDataLocal(primeiroDia),
            data_fim: formatarDataLocal(hoje)
        });
    }, []);

    useEffect(() => {
        loadCategorias();
    }, []);

    useEffect(() => {
        loadClientes();
        loadProcessos();
    }, []);

    useEffect(() => {
        if (!hasAccess) return;
        
        if (canViewBasic) {
            loadLancamentos();
        }
        
        if (canViewStats) {
            loadEstatisticas();
        }
        
        if (activeTab === 1 && canViewBasic) {
            loadDividas();
        }
        if (activeTab === 2 && canViewReports) {
            loadResumoMensal();
            loadContasReceberPagar();
        }
        if (activeTab === 3 && canViewReports) {
            loadFluxoMensal();
        }
    }, [filtros, page, rowsPerPage, activeTab, mesSelecionado, anoRelatorio, hasAccess, canViewBasic, canViewStats, canViewReports]);

    const loadLancamentos = async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                ...filtros,
                limit: rowsPerPage.toString(),
                offset: (page * rowsPerPage).toString()
            });
            
            Array.from(params.entries()).forEach(([key, value]) => {
                if (!value) params.delete(key);
            });

            const response = await api.get(`/fluxo-caixa/lancamentos?${params.toString()}`);
            setLancamentos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lançamentos:', error);
            setError(error.response?.data?.error || 'Erro ao carregar lançamentos');
        } finally {
            setLoading(false);
        }
    };

    const loadCategorias = async () => {
        try {
            const response = await api.get('/categorias-contabeis/agrupadas');
            setCategoriasAgrupadas(response.data || { RECEITA: {}, DESPESA: {} });
            
            // Também carrega lista simples para filtros
            const responseLista = await api.get('/categorias-contabeis?ativo=true');
            setCategorias(responseLista.data || []);
        } catch (error) {
            console.error('Erro ao carregar categorias:', error);
        }
    };

    const loadClientes = async () => {
        try {
            const response = await api.get('/clientes?limit=1000');
            setClientes(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar clientes:', error);
        }
    };

    const loadProcessos = async () => {
        try {
            const response = await api.get('/processos?limit=1000');
            setProcessos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar processos:', error);
        }
    };

    const loadDividas = async () => {
        try {
            const params = new URLSearchParams({ ativo: 'true' });
            const response = await api.get(`/dividas?${params.toString()}`);
            setDividas(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar dívidas:', error);
        }
    };

    const loadResumoMensal = async () => {
        try {
            console.log(`[RELATORIO] Carregando resumo mensal para ano: ${anoRelatorio}`);
            const response = await api.get(`/fluxo-caixa/resumo-mensal?ano=${anoRelatorio}`);
            console.log(`[RELATORIO] ✅ Resumo mensal carregado: ${response.data?.length || 0} meses`);
            setResumoMensal(response.data || []);
        } catch (error) {
            console.error('[RELATORIO] ❌ Erro ao carregar resumo mensal:', error);
            setError(error.response?.data?.error || 'Erro ao carregar resumo mensal');
        }
    };

    const loadContasReceberPagar = async () => {
        try {
            const [receber, pagar] = await Promise.all([
                api.get('/fluxo-caixa/contas-receber'),
                api.get('/fluxo-caixa/contas-pagar')
            ]);
            console.log(`[RELATORIO] ✅ Contas a receber: ${receber.data?.length || 0}, Contas a pagar: ${pagar.data?.length || 0}`);
            setContasReceber(receber.data || []);
            setContasPagar(pagar.data || []);
        } catch (error) {
            console.error('[RELATORIO] ❌ Erro ao carregar contas:', error);
            setError(error.response?.data?.error || 'Erro ao carregar contas');
        }
    };

    const loadFluxoMensal = async () => {
        if (!mesSelecionado) {
            setFluxoMensal([]);
            return;
        }
        setLoading(true);
        try {
            // mesSelecionado está no formato YYYY-MM (input type="month")
            const [ano, mes] = mesSelecionado.split('-');
            if (!mes || !ano) {
                console.error('[FLUXO_CAIXA] Formato de mês/ano inválido:', mesSelecionado);
                setFluxoMensal([]);
                setLoading(false);
                return;
            }
            console.log(`[FLUXO_CAIXA] Buscando movimentações para mês: ${mes}, ano: ${ano}`);
            const response = await api.get(`/fluxo-caixa/fluxo-detalhado?mes=${mes}&ano=${ano}`);
            console.log(`[FLUXO_CAIXA] ✅ Carregado: ${response.data?.length || 0} movimentações para ${mes}/${ano}`);
            if (response.data && response.data.length > 0) {
                console.log(`[FLUXO_CAIXA] Primeira movimentação:`, response.data[0]);
            }
            setFluxoMensal(response.data || []);
        } catch (error) {
            console.error('[FLUXO_CAIXA] ❌ Erro ao carregar fluxo mensal:', error);
            setError(error.response?.data?.error || 'Erro ao carregar fluxo de caixa detalhado');
            setFluxoMensal([]);
        } finally {
            setLoading(false);
        }
    };

    const getMesNome = (mes) => {
        const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                       'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
        return meses[parseInt(mes) - 1] || mes;
    };

    const toggleDividaExpand = (dividaId) => {
        setExpandedDividas(prev => ({
            ...prev,
            [dividaId]: !prev[dividaId]
        }));
    };

    const agruparDividasPorCodigo = (dividas) => {
        const grupos = {};
        dividas.forEach(d => {
            // Usa codigo_original se existir, senão pega a parte base do código (antes do hífen)
            let codigoBase = d.codigo_original;
            if (!codigoBase && d.codigo) {
                const partes = d.codigo.split('-');
                codigoBase = partes[0] || d.codigo;
            }
            if (!codigoBase) {
                codigoBase = `DIV-${d.id}`; // Fallback se não houver código
            }
            
            if (!grupos[codigoBase]) {
                grupos[codigoBase] = [];
            }
            grupos[codigoBase].push(d);
        });
        return grupos;
    };

    const loadEstatisticas = async () => {
        try {
            const params = new URLSearchParams(filtros);
            Array.from(params.entries()).forEach(([key, value]) => {
                if (!value) params.delete(key);
            });

            const response = await api.get(`/fluxo-caixa/estatisticas?${params.toString()}`);
            const stats = response.data || {};
            
            // Carrega totais de dívidas
            const dividasParams = new URLSearchParams({ ativo: 'true' });
            const dividasResponse = await api.get(`/dividas?${dividasParams.toString()}`);
            const todasDividas = dividasResponse.data || [];
            
            const totalReceber = todasDividas
                .filter(d => d.tipo === 'RECEBER' && (d.status === 'PENDENTE' || d.status === 'PARCELADA'))
                .reduce((sum, d) => sum + parseFloat(d.valor_parcela || d.valor_total || 0), 0);
            
            const totalPagar = todasDividas
                .filter(d => d.tipo === 'PAGAR' && (d.status === 'PENDENTE' || d.status === 'PARCELADA'))
                .reduce((sum, d) => sum + parseFloat(d.valor_parcela || d.valor_total || 0), 0);

            setEstatisticas({
                ...stats,
                total_receber: totalReceber,
                total_pagar: totalPagar
            });
        } catch (error) {
            console.error('Erro ao carregar estatísticas:', error);
        }
    };

    const handleFilterChange = (field, value) => {
        setFiltros({ ...filtros, [field]: value });
        setPage(0);
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                data: item.data,
                tipo: item.tipo,
                categoria: item.categoria || '',
                descricao: item.descricao,
                valor: item.valor,
                observacoes: item.observacoes || '',
                divida_id: item.divida_id || null,
                cliente_id: item.cliente_id || null,
                processo_id: item.processo_id || null
            });
        } else {
            setEditingItem(null);
            setFormData({
                data: (() => {
            const hoje = new Date();
            const ano = hoje.getFullYear();
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const dia = String(hoje.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        })(),
                tipo: '',
                categoria: '',
                descricao: '',
                valor: '',
                observacoes: '',
                divida_id: null,
                cliente_id: null,
                processo_id: null
            });
        }
        setDialogOpen(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingItem(null);
        setError('');
    };

    const handleSave = async () => {
        if (!formData.data || !formData.tipo || !formData.descricao || !formData.valor) {
            setError('Preencha todos os campos obrigatórios');
            return;
        }

        try {
            const dataToSend = {
                ...formData,
                valor: parseFloat(formData.valor)
            };

            if (editingItem) {
                await api.put(`/fluxo-caixa/lancamentos/${editingItem.id}`, dataToSend);
            } else {
                await api.post('/fluxo-caixa/lancamentos', dataToSend);
            }

            handleCloseDialog();
            loadLancamentos();
            loadEstatisticas();
        } catch (error) {
            console.error('Erro ao salvar lançamento:', error);
            setError(error.response?.data?.error || 'Erro ao salvar lançamento');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este lançamento?')) {
            try {
                await api.delete(`/fluxo-caixa/lancamentos/${id}`);
                loadLancamentos();
                loadEstatisticas();
            } catch (error) {
                console.error('Erro ao deletar lançamento:', error);
                alert('Erro ao deletar lançamento: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const [dividaFormData, setDividaFormData] = useState({
        cliente_id: '',
        processo_id: '',
        valor_total: '',
        data_vencimento: '',
        data_emissao: (() => {
            const hoje = new Date();
            const ano = hoje.getFullYear();
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const dia = String(hoje.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        })(),
        observacoes: '',
        descricao: '',
        tipo: 'RECEBER',
        parcelar: false,
        numero_parcelas: 1,
        intervalo_dias: 30
    });

    const [baixaFormData, setBaixaFormData] = useState({
        valor: '',
        data: (() => {
            const hoje = new Date();
            const ano = hoje.getFullYear();
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const dia = String(hoje.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        })(),
        observacoes: ''
    });

    const handleSaveDivida = async () => {
        if (!dividaFormData.descricao || !dividaFormData.valor_total || !dividaFormData.data_vencimento || !dividaFormData.data_emissao) {
            setError('Preencha todos os campos obrigatórios');
            return;
        }

        try {
            const dataToSend = {
                ...dividaFormData,
                valor_total: parseFloat(dividaFormData.valor_total),
                numero_parcelas: dividaFormData.parcelar ? parseInt(dividaFormData.numero_parcelas) || 1 : 1,
                cliente_id: dividaFormData.cliente_id || null,
                processo_id: dividaFormData.processo_id || null
            };

            await api.post('/dividas', dataToSend);
            setDividaDialogOpen(false);
            setDividaFormData({
                cliente_id: '',
                processo_id: '',
                valor_total: '',
                data_vencimento: '',
                data_emissao: (() => {
            const hoje = new Date();
            const ano = hoje.getFullYear();
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const dia = String(hoje.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        })(),
                observacoes: '',
                descricao: '',
                tipo: 'RECEBER',
                parcelar: false,
                numero_parcelas: 1,
                intervalo_dias: 30
            });
            loadDividas();
            loadEstatisticas();
        } catch (error) {
            console.error('Erro ao salvar dívida:', error);
            setError(error.response?.data?.error || 'Erro ao salvar dívida');
        }
    };

    const handleBaixaDivida = async () => {
        if (!baixaFormData.valor || !baixaFormData.data || !dividaSelecionada) {
            setError('Preencha todos os campos obrigatórios');
            return;
        }

        try {
            const valorBaixa = parseFloat(baixaFormData.valor);
            const valorParcela = parseFloat(dividaSelecionada.valor_parcela || dividaSelecionada.valor_total);
            
            if (isNaN(valorBaixa) || valorBaixa <= 0) {
                setError('Valor inválido. Informe um valor maior que zero.');
                return;
            }

            const desconto = Math.max(0, valorParcela - valorBaixa);

            // Criar lançamento contábil de baixa (com possível desconto)
            const lancamentoBaixa = {
                data: baixaFormData.data,
                tipo: 'BAIXA_DIVIDA', // Usa tipo específico para baixa de dívida
                descricao: `Baixa de dívida ${dividaSelecionada.codigo || dividaSelecionada.codigo_divida} - ${baixaFormData.observacoes || (desconto > 0 ? `Pagamento com desconto de R$ ${desconto.toFixed(2)}` : 'Pagamento de dívida')}`,
                valor: valorBaixa,
                desconto,
                divida_id: dividaSelecionada.id,
                cliente_id: dividaSelecionada.cliente_id,
                processo_id: dividaSelecionada.processo_id,
                observacoes: baixaFormData.observacoes
            };

            // Atualizar status da dívida se o valor pago + desconto for igual ou maior que o valor da parcela
            const novoStatus = valorBaixa + desconto >= valorParcela ? 'PAGA' : dividaSelecionada.status;
            
            // Cria o lançamento (que automaticamente atualiza o status se for BAIXA_DIVIDA e valor >= parcela)
            // Mas vamos atualizar manualmente para garantir que funcione mesmo com pagamentos parciais
            await api.post('/fluxo-caixa/lancamentos', lancamentoBaixa);
            
            // Atualizar dívida manualmente (a atualização automática só acontece se for PAGA completa)
            if (novoStatus !== dividaSelecionada.status) {
                await api.put(`/dividas/${dividaSelecionada.id}`, {
                    status: novoStatus
                });
            }

            setBaixaDialogOpen(false);
            setDividaSelecionada(null);
            setBaixaFormData({
                valor: '',
                data: (() => {
            const hoje = new Date();
            const ano = hoje.getFullYear();
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const dia = String(hoje.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        })(),
                observacoes: ''
            });
            
            // Recarrega todos os dados após baixa
            await Promise.all([
                loadDividas(),
                loadLancamentos(),
                loadEstatisticas(),
                // Sempre recarrega dados do relatório para garantir sincronização
                loadResumoMensal(),
                loadContasReceberPagar()
            ]);
            
            // Se estiver na aba de fluxo detalhado, verifica se precisa atualizar
            // Se a data da baixa corresponde ao mês selecionado, recarrega automaticamente
            if (activeTab === 3) {
                const dataBaixa = new Date(baixaFormData.data + 'T00:00:00');
                const mesBaixaFormatado = `${dataBaixa.getFullYear()}-${String(dataBaixa.getMonth() + 1).padStart(2, '0')}`;
                
                // Se o mês da baixa corresponde ao mês selecionado, recarrega
                if (mesBaixaFormatado === mesSelecionado) {
                    console.log(`[BAIXA_DIVIDA] ✅ Recarregando fluxo para o mês da baixa: ${mesBaixaFormatado}`);
                    await loadFluxoMensal();
                } else {
                    console.log(`[BAIXA_DIVIDA] ⚠️ Data da baixa (${mesBaixaFormatado}) diferente do mês selecionado (${mesSelecionado}).`);
                    console.log(`[BAIXA_DIVIDA] A movimentação aparecerá no Fluxo de Caixa quando você selecionar o mês: ${mesBaixaFormatado}`);
                }
            }
            
            setError(''); // Limpa erros anteriores
        } catch (error) {
            console.error('Erro ao baixar dívida:', error);
            setError(error.response?.data?.error || 'Erro ao baixar dívida');
        }
    };

    const handleDeleteDivida = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta dívida?')) {
            try {
                await api.delete(`/dividas/${id}`);
                loadDividas();
                loadEstatisticas();
            } catch (error) {
                console.error('Erro ao deletar dívida:', error);
                alert('Erro ao deletar dívida: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Se a data está no formato YYYY-MM-DD, extrai diretamente sem conversão de timezone
            // Isso evita problemas de timezone que podem mostrar um dia anterior
            if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
                const partes = date.split('T')[0].split(' ')[0].split('-');
                if (partes.length === 3) {
                    // Retorna DD/MM/YYYY
                    return `${partes[2]}/${partes[1]}/${partes[0]}`;
                }
            }
            // Fallback: tenta usar Date (pode ter problema de timezone)
            const d = new Date(date);
            return d.toLocaleDateString('pt-BR');
        } catch {
            return date;
        }
    };

    // Função para obter lista plana de categorias do tipo selecionado
    const getCategoriasLista = () => {
        if (!formData.tipo || formData.tipo === 'BAIXA_DIVIDA' || !categoriasAgrupadas[formData.tipo]) {
            return [];
        }
        
        const lista = [];
        Object.entries(categoriasAgrupadas[formData.tipo]).forEach(([grupo, subgrupos]) => {
            Object.entries(subgrupos).forEach(([subgrupo, cats]) => {
                cats.forEach((cat) => {
                    lista.push({
                        id: cat.id,
                        nome: cat.nome,
                        grupo: grupo,
                        subgrupo: subgrupo
                    });
                });
            });
        });
        return lista;
    };

    const getTipoColor = (tipo) => {
        switch (tipo) {
            case 'RECEITA': return 'success';
            case 'DESPESA': return 'error';
            case 'BAIXA_DIVIDA': return 'info';
            default: return 'default';
        }
    };

    const getTipoLabel = (tipo) => {
        switch (tipo) {
            case 'RECEITA': return 'Receita';
            case 'DESPESA': return 'Despesa';
            case 'BAIXA_DIVIDA': return 'Baixa Dívida';
            default: return tipo;
        }
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Fluxo de Caixa
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Gestão de lançamentos contábeis e controle financeiro
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Cards de Estatísticas - apenas se tiver permissão */}
            {canViewStats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
                            <CardContent>
                                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    Receitas
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(estatisticas.total_receitas)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white' }}>
                            <CardContent>
                                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    Despesas
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(estatisticas.total_despesas)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
                            <CardContent>
                                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    Saldo Líquido
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(estatisticas.saldo_liquido)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
                            <CardContent>
                                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    A Receber
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(estatisticas.total_receber)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                        <Card sx={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white' }}>
                            <CardContent>
                                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                    A Pagar
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(estatisticas.total_pagar)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Tabs - apenas se tiver acesso */}
            {hasAccess && !checkingPermissions && (
                <Paper sx={{ mb: 3 }}>
                    <Tabs 
                        value={getTabValue()} 
                        onChange={(e, newValue) => {
                            // Mapeia o índice do Tabs para o activeTab real
                            const realIndex = mapTabIndexToActiveTab(newValue);
                            
                            // Valida permissões ao mudar de tab
                            if (realIndex === 2 || realIndex === 3) {
                                // Tabs 2 (Relatório Geral) e 3 (Fluxo de Caixa) requerem permissão 'reports'
                                if (!canViewReports && !isAdmin()) {
                                    setError('Você não tem permissão para acessar esta área.');
                                    return;
                                }
                            }
                            if (realIndex === 0 || realIndex === 1) {
                                // Tabs 0 (Lançamentos) e 1 (Dívidas) requerem permissão 'view' ou 'read'
                                if (!canViewBasic && !isAdmin()) {
                                    setError('Você não tem permissão para acessar esta área.');
                                    return;
                                }
                            }
                            setActiveTab(realIndex);
                        }}
                    >
                        {canViewBasic && (
                            <Tab icon={<ReceiptIcon />} iconPosition="start" label="Lançamentos" />
                        )}
                        {canViewBasic && (
                            <Tab icon={<AccountBalanceWalletIcon />} iconPosition="start" label="Dívidas e Parcelamentos" />
                        )}
                        {canViewReports && (
                            <Tab icon={<AssessmentIcon />} iconPosition="start" label="Relatório Geral" />
                        )}
                        {canViewReports && (
                            <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Fluxo de Caixa" />
                        )}
                    </Tabs>
                </Paper>
            )}

            {/* Mensagem de verificação de permissões */}
            {checkingPermissions && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Mensagem quando não tem acesso ao Fluxo de Caixa */}
            {!hasAccess && !checkingPermissions && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Você não tem permissão para acessar o Fluxo de Caixa.
                    </Alert>
                    <Typography variant="body1" color="text.secondary">
                        Entre em contato com o administrador do sistema para solicitar acesso.
                    </Typography>
                </Paper>
            )}

            {/* Mensagem quando tem acesso mas não tem permissão para nenhuma área */}
            {hasAccess && !checkingPermissions && !canViewBasic && !canViewReports && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Você tem acesso ao Fluxo de Caixa, mas não possui permissões para visualizar nenhuma área.
                    </Alert>
                    <Typography variant="body1" color="text.secondary">
                        Entre em contato com o administrador do sistema para configurar suas permissões.
                    </Typography>
                </Paper>
            )}

            {/* Tab Content - Lançamentos */}
            {hasAccess && canViewBasic && activeTab === 0 && (
                <>
                    {/* Filtros */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Filtros</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}
                            >
                                Novo Lançamento
                            </Button>
                        </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Data Início"
                            type="date"
                            value={filtros.data_inicio}
                            onChange={(e) => handleFilterChange('data_inicio', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Data Fim"
                            type="date"
                            value={filtros.data_fim}
                            onChange={(e) => handleFilterChange('data_fim', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Tipo"
                            value={filtros.tipo}
                            onChange={(e) => handleFilterChange('tipo', e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="RECEITA">Receita</MenuItem>
                            <MenuItem value="DESPESA">Despesa</MenuItem>
                            <MenuItem value="BAIXA_DIVIDA">Baixa Dívida</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Categoria"
                            value={filtros.categoria}
                            onChange={(e) => handleFilterChange('categoria', e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {categorias.map((cat) => (
                                <MenuItem key={cat.id} value={cat.nome}>
                                    {cat.nome}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabela de Lançamentos */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Categoria</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="right">Débito</TableCell>
                            <TableCell align="right">Crédito</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : lancamentos.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Nenhum lançamento encontrado
                                </TableCell>
                            </TableRow>
                        ) : (
                            lancamentos.map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>{formatDate(item.data)}</TableCell>
                                    <TableCell>{item.descricao}</TableCell>
                                    <TableCell>{item.categoria || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getTipoLabel(item.tipo)}
                                            color={getTipoColor(item.tipo)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>
                                        {item.tipo === 'RECEITA' ? formatCurrency(item.valor) : '-'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#ef4444', fontWeight: 600 }}>
                                        {item.tipo === 'DESPESA' || item.tipo === 'BAIXA_DIVIDA' ? formatCurrency(item.valor) : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenDialog(item)} color="primary">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={lancamentos.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Registros por página:"
                />
            </TableContainer>
                </>
            )}

            {/* Tab Content - Dívidas e Parcelamentos */}
            {hasAccess && canViewBasic && activeTab === 1 && (
                <>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Dívidas Ativas</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setDividaDialogOpen(true)}
                                sx={{ backgroundColor: '#8b5cf6', '&:hover': { backgroundColor: '#7c3aed' } }}
                            >
                                Nova Dívida
                            </Button>
                        </Box>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Filtrar por cliente"
                                    placeholder="Digite o nome do cliente"
                                    value={filtroClienteDivida}
                                    onChange={(e) => setFiltroClienteDivida(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    select
                                    label="Status"
                                    value={filtroStatusDivida}
                                    onChange={(e) => setFiltroStatusDivida(e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="PENDENTE">Pendente</MenuItem>
                                    <MenuItem value="PARCELADA">Parcelada</MenuItem>
                                    <MenuItem value="PAGA">Paga</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Paper>

                    {(() => {
                        // Aplica filtros
                        const dividasFiltradas = dividas.filter(divida => {
                            // Filtro por cliente
                            if (filtroClienteDivida) {
                                const clienteNome = divida.cliente_id ? 
                                    clientes.find(c => c.id === divida.cliente_id)?.nome || '' : '';
                                if (!clienteNome.toLowerCase().includes(filtroClienteDivida.toLowerCase())) {
                                    return false;
                                }
                            }
                            
                            // Filtro por status
                            if (filtroStatusDivida && divida.status !== filtroStatusDivida) {
                                return false;
                            }
                            
                            return true;
                        });
                        
                        const dividasAgrupadas = agruparDividasPorCodigo(dividasFiltradas);
                        
                        return Object.keys(dividasAgrupadas).length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Nenhuma dívida encontrada {filtroClienteDivida || filtroStatusDivida ? 'com os filtros aplicados' : ''}
                                </Typography>
                            </Paper>
                        ) : (
                            Object.entries(dividasAgrupadas).map(([codigoBase, parcelas]) => {
                            const primeiraParcela = parcelas[0];
                            // Cada registro de parcela guarda em valor_total o valor total da dívida;
                            // por isso usamos apenas o valor_total da primeira parcela para não multiplicar.
                            const totalValor = parseFloat(primeiraParcela.valor_total || 0);
                            // Para o que já foi quitado, consideramos o valor CONTRATADO da parcela (valor_parcela),
                            // independentemente de ter havido desconto no pagamento.
                            const valorPago = parcelas
                                .filter(p => p.status === 'PAGA')
                                .reduce((sum, p) => sum + parseFloat(p.valor_parcela || p.valor_total || 0), 0);
                            const valorPendente = totalValor - valorPago;
                            const clienteNome = primeiraParcela.cliente_id ? 
                                clientes.find(c => c.id === primeiraParcela.cliente_id)?.nome || '-' : '-';

                            return (
                                <Accordion key={codigoBase} sx={{ mb: 2 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {clienteNome} - {primeiraParcela.descricao}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {codigoBase} | Total: {formatCurrency(totalValor)} | 
                                                    Pendente: <strong style={{ color: '#ef4444' }}>{formatCurrency(valorPendente)}</strong>
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Chip
                                                    label={primeiraParcela.status || 'PENDENTE'}
                                                    color={primeiraParcela.status === 'PAGA' ? 'success' : primeiraParcela.status === 'PARCELADA' ? 'info' : 'warning'}
                                                    size="small"
                                                />
                                                <Chip
                                                    label={primeiraParcela.tipo === 'RECEBER' ? 'A Receber' : 'A Pagar'}
                                                    color={primeiraParcela.tipo === 'RECEBER' ? 'primary' : 'secondary'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Parcela</TableCell>
                                                        <TableCell>Vencimento</TableCell>
                                                        <TableCell align="right">Valor</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell align="center">Ações</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {parcelas.sort((a, b) => (a.numero_parcela || 0) - (b.numero_parcela || 0)).map((parcela) => (
                                                        <TableRow key={parcela.id} hover>
                                                            <TableCell>{parcela.codigo || `${codigoBase}-${parcela.numero_parcela || 0}`}</TableCell>
                                                            <TableCell>{formatDate(parcela.data_vencimento)}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                {formatCurrency(parcela.valor_parcela || parcela.valor_total)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={parcela.status === 'PAGA' ? '✓ Paga' : '⏳ Pendente'}
                                                                    color={parcela.status === 'PAGA' ? 'success' : 'warning'}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {parcela.status !== 'PAGA' && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setDividaSelecionada(parcela);
                                                                            // Reseta formulário de baixa sempre que abrir um novo diálogo
                                                                            const hoje = new Date();
                                                                            const ano = hoje.getFullYear();
                                                                            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
                                                                            const dia = String(hoje.getDate()).padStart(2, '0');
                                                                            setBaixaFormData({
                                                                                valor: '',
                                                                                data: `${ano}-${mes}-${dia}`,
                                                                                observacoes: ''
                                                                            });
                                                                            setError('');
                                                                            setBaixaDialogOpen(true);
                                                                        }}
                                                                        color="success"
                                                                        sx={{ mr: 1 }}
                                                                    >
                                                                        <CheckCircleIcon fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteDivida(parcela.id)}
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                    )})()}
                </>
            )}

            {/* Tab Content - Relatório Geral */}
            {hasAccess && canViewReports && activeTab === 2 && (
                <>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Resumo por Mês</Typography>
                            <TextField
                                label="Ano"
                                type="number"
                                size="small"
                                value={anoRelatorio}
                                onChange={(e) => setAnoRelatorio(e.target.value)}
                                inputProps={{ min: 2000, max: 2100 }}
                                sx={{ width: 120 }}
                            />
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Mês</TableCell>
                                        <TableCell align="right">Receitas</TableCell>
                                        <TableCell align="right">Despesas</TableCell>
                                        <TableCell align="right">Dívidas Criadas</TableCell>
                                        <TableCell align="right">Saldo</TableCell>
                                        <TableCell align="center">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {resumoMensal.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                Nenhum dado encontrado
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        resumoMensal.map((mes) => (
                                            <TableRow key={`${mes.ano}-${mes.mes}`} hover>
                                                <TableCell>
                                                    <strong>{getMesNome(mes.mes)} {mes.ano}</strong>
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>
                                                    {formatCurrency(mes.receitas || 0)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#ef4444', fontWeight: 600 }}>
                                                    {formatCurrency(mes.despesas || 0)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                                                    {formatCurrency(mes.dividas_criadas || 0)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {formatCurrency(mes.saldo || 0)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={mes.saldo >= 0 ? '✅ Superávit' : '❌ Déficit'}
                                                        color={mes.saldo >= 0 ? 'success' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Contas a Receber</Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>Descrição</TableCell>
                                        <TableCell>Vencimento</TableCell>
                                        <TableCell align="right">Valor Total</TableCell>
                                        <TableCell align="right">Recebido</TableCell>
                                        <TableCell align="right">Desconto</TableCell>
                                        <TableCell align="right">Pendente</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contasReceber.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Sem contas a receber pendentes
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    Todas as dívidas do tipo "A Receber" foram pagas ou não há dívidas ativas
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        contasReceber.map((conta) => (
                                            <TableRow key={conta.id} hover>
                                                <TableCell>{conta.cliente_nome || '-'}</TableCell>
                                                <TableCell>{conta.descricao}</TableCell>
                                                <TableCell>{formatDate(conta.data_vencimento)}</TableCell>
                                                <TableCell align="right">{formatCurrency(conta.valor_total || conta.valor_parcela)}</TableCell>
                                                <TableCell align="right">{formatCurrency(conta.valor_recebido || 0)}</TableCell>
                                                <TableCell align="right">{formatCurrency(conta.desconto_concedido || 0)}</TableCell>
                                                <TableCell align="right" sx={{ color: '#ef4444', fontWeight: 600 }}>
                                                    {formatCurrency(conta.valor_pendente || 0)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Contas a Pagar</Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fornecedor</TableCell>
                                        <TableCell>Descrição</TableCell>
                                        <TableCell>Vencimento</TableCell>
                                        <TableCell align="right">Valor Total</TableCell>
                                        <TableCell align="right">Pago</TableCell>
                                        <TableCell align="right">Desconto</TableCell>
                                        <TableCell align="right">Pendente</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contasPagar.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Sem contas a pagar pendentes
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    Todas as dívidas foram pagas ou não há dívidas ativas
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        contasPagar.map((conta) => (
                                            <TableRow key={conta.id} hover>
                                                <TableCell>{conta.cliente_nome || '-'}</TableCell>
                                                <TableCell>{conta.descricao}</TableCell>
                                                <TableCell>{formatDate(conta.data_vencimento)}</TableCell>
                                                <TableCell align="right">{formatCurrency(conta.valor_total || conta.valor_parcela)}</TableCell>
                                                <TableCell align="right">{formatCurrency(conta.valor_pago || 0)}</TableCell>
                                                <TableCell align="right">{formatCurrency(conta.desconto_concedido || 0)}</TableCell>
                                                <TableCell align="right" sx={{ color: '#ef4444', fontWeight: 600 }}>
                                                    {formatCurrency(conta.valor_pendente || 0)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}

            {/* Tab Content - Fluxo de Caixa Detalhado */}
            {hasAccess && canViewReports && activeTab === 3 && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6">Fluxo de Caixa Detalhado</Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                label="Mês/Ano"
                                type="month"
                                size="small"
                                value={mesSelecionado}
                                onChange={(e) => {
                                    const novoMes = e.target.value;
                                    console.log('[FLUXO_CAIXA] Mês selecionado alterado:', novoMes);
                                    setMesSelecionado(novoMes);
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 200 }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={() => {
                                    console.log('[FLUXO_CAIXA] Recarregando fluxo para:', mesSelecionado);
                                    loadFluxoMensal();
                                }}
                            >
                                Atualizar
                            </Button>
                        </Box>
                    </Box>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Data</TableCell>
                                        <TableCell>Descrição</TableCell>
                                        <TableCell>Categoria</TableCell>
                                        <TableCell align="right">Entrada (Débito)</TableCell>
                                        <TableCell align="right">Saída (Crédito)</TableCell>
                                        <TableCell align="right">Saldo Acumulado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fluxoMensal.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Nenhuma transação encontrada para {mesSelecionado ? getMesNome(mesSelecionado.split('-')[1]) + ' ' + mesSelecionado.split('-')[0] : 'este mês'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    💡 Dica: A movimentação aparece no mês da <strong>data do lançamento</strong>, não no mês da dívida.
                                                    <br />Se você acabou de baixar uma dívida, verifique se o mês selecionado corresponde à <strong>data do pagamento</strong> informada.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        (() => {
                                            let saldoAcumulado = 0;
                                            return fluxoMensal.map((item, index) => {
                                                const entrada = item.tipo === 'RECEITA' ? parseFloat(item.valor || 0) : 0;
                                                const saida = item.tipo === 'DESPESA' || item.tipo === 'BAIXA_DIVIDA' ? parseFloat(item.valor || 0) : 0;
                                                saldoAcumulado += (entrada - saida);
                                                return (
                                                    <TableRow key={index} hover>
                                                        <TableCell>{formatDate(item.data)}</TableCell>
                                                        <TableCell>{item.descricao}</TableCell>
                                                        <TableCell>{item.categoria || '-'}</TableCell>
                                                        <TableCell align="right" sx={{ color: entrada > 0 ? '#10b981' : 'inherit', fontWeight: entrada > 0 ? 600 : 'normal' }}>
                                                            {entrada > 0 ? formatCurrency(entrada) : '-'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ color: saida > 0 ? '#ef4444' : 'inherit', fontWeight: saida > 0 ? 600 : 'normal' }}>
                                                            {saida > 0 ? formatCurrency(saida) : '-'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 600, color: saldoAcumulado >= 0 ? '#10b981' : '#ef4444' }}>
                                                            {formatCurrency(saldoAcumulado)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            });
                                        })()
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {/* Dialog para Criar/Editar Lançamento */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Lançamento' : 'Novo Lançamento Contábil'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data *"
                                    type="date"
                                    value={formData.data}
                                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Tipo *"
                                    value={formData.tipo}
                                    onChange={(e) => {
                                        setFormData({ ...formData, tipo: e.target.value, categoria: '' });
                                    }}
                                >
                                    <MenuItem value="">Selecione...</MenuItem>
                                    <MenuItem value="RECEITA">Receita (Débito)</MenuItem>
                                    <MenuItem value="DESPESA">Despesa (Crédito)</MenuItem>
                                    <MenuItem value="BAIXA_DIVIDA">Baixa Dívida</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Categoria"
                                    value={formData.categoria || ''}
                                    onChange={(e) => {
                                        const valor = e.target.value;
                                        console.log('Categoria selecionada:', valor); // Debug
                                        setFormData(prev => ({ ...prev, categoria: valor }));
                                    }}
                                    disabled={!formData.tipo || formData.tipo === 'BAIXA_DIVIDA'}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 400,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Selecione uma categoria...</em>
                                    </MenuItem>
                                    {getCategoriasLista().map((cat) => (
                                        <MenuItem 
                                            key={cat.id} 
                                            value={cat.nome}
                                        >
                                            {cat.nome}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Valor (R$) *"
                                    type="number"
                                    value={formData.valor}
                                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                                    inputProps={{ step: '0.01', min: '0' }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrição *"
                                    value={formData.descricao}
                                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Observações"
                                    value={formData.observacoes}
                                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para Criar Dívida */}
            <Dialog open={dividaDialogOpen} onClose={() => setDividaDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Nova Dívida</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Tipo de Dívida *"
                                    value={dividaFormData.tipo}
                                    onChange={(e) => setDividaFormData({ ...dividaFormData, tipo: e.target.value })}
                                >
                                    <MenuItem value="RECEBER">Conta a Receber</MenuItem>
                                    <MenuItem value="PAGAR">Conta a Pagar</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    options={clientes}
                                    getOptionLabel={(option) => option.nome || ''}
                                    value={clientes.find(c => c.id === dividaFormData.cliente_id) || null}
                                    onChange={(event, newValue) => {
                                        setDividaFormData({ 
                                            ...dividaFormData, 
                                            cliente_id: newValue ? newValue.id : '' 
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Cliente/Fornecedor *"
                                            required
                                        />
                                    )}
                                    filterOptions={(options, params) => {
                                        const filtered = options.filter((option) =>
                                            option.nome.toLowerCase().includes(params.inputValue.toLowerCase())
                                        );
                                        return filtered;
                                    }}
                                    noOptionsText="Nenhum cliente encontrado"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    options={processos}
                                    getOptionLabel={(option) => option.numero_processo || ''}
                                    value={processos.find(p => p.id === dividaFormData.processo_id) || null}
                                    onChange={(event, newValue) => {
                                        setDividaFormData({ 
                                            ...dividaFormData, 
                                            processo_id: newValue ? newValue.id : '' 
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Processo (Opcional)"
                                            placeholder="Digite para pesquisar..."
                                        />
                                    )}
                                    filterOptions={(options, params) => {
                                        const filtered = options.filter((option) =>
                                            option.numero_processo?.toLowerCase().includes(params.inputValue.toLowerCase())
                                        );
                                        return filtered;
                                    }}
                                    noOptionsText="Nenhum processo encontrado"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrição *"
                                    value={dividaFormData.descricao}
                                    onChange={(e) => setDividaFormData({ ...dividaFormData, descricao: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Valor Total (R$) *"
                                    type="number"
                                    value={dividaFormData.valor_total}
                                    onChange={(e) => setDividaFormData({ ...dividaFormData, valor_total: e.target.value })}
                                    inputProps={{ step: '0.01', min: '0' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data de Emissão *"
                                    type="date"
                                    value={dividaFormData.data_emissao}
                                    onChange={(e) => setDividaFormData({ ...dividaFormData, data_emissao: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data de Vencimento *"
                                    type="date"
                                    value={dividaFormData.data_vencimento}
                                    onChange={(e) => setDividaFormData({ ...dividaFormData, data_vencimento: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Parcelar?"
                                    value={dividaFormData.parcelar ? 'sim' : 'nao'}
                                    onChange={(e) => setDividaFormData({ ...dividaFormData, parcelar: e.target.value === 'sim' })}
                                >
                                    <MenuItem value="nao">Não (À vista)</MenuItem>
                                    <MenuItem value="sim">Sim</MenuItem>
                                </TextField>
                            </Grid>
                            {dividaFormData.parcelar && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Número de Parcelas"
                                            type="number"
                                            value={dividaFormData.numero_parcelas}
                                            onChange={(e) => setDividaFormData({ ...dividaFormData, numero_parcelas: e.target.value })}
                                            inputProps={{ min: '2', max: '12' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Intervalo entre Parcelas (dias)"
                                            type="number"
                                            value={dividaFormData.intervalo_dias}
                                            onChange={(e) => setDividaFormData({ ...dividaFormData, intervalo_dias: e.target.value })}
                                            inputProps={{ min: '1' }}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Observações"
                                    value={dividaFormData.observacoes}
                                    onChange={(e) => setDividaFormData({ ...dividaFormData, observacoes: e.target.value })}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDividaDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveDivida} variant="contained" sx={{ backgroundColor: '#8b5cf6', '&:hover': { backgroundColor: '#7c3aed' } }}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para Baixar Dívida */}
            <Dialog open={baixaDialogOpen} onClose={() => setBaixaDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Registrar Baixa / Pagamento</DialogTitle>
                <DialogContent>
                    {dividaSelecionada && (
                        <Box sx={{ pt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Dívida:</strong> {dividaSelecionada.codigo || dividaSelecionada.codigo_divida}<br />
                                    <strong>Valor da Parcela:</strong> {formatCurrency(dividaSelecionada.valor_parcela || dividaSelecionada.valor_total)}
                                </Typography>
                            </Alert>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Data do Pagamento *"
                                        type="date"
                                        value={baixaFormData.data}
                                        onChange={(e) => setBaixaFormData({ ...baixaFormData, data: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Valor do Pagamento (R$) *"
                                        type="number"
                                        value={baixaFormData.valor}
                                        onChange={(e) => setBaixaFormData({ ...baixaFormData, valor: e.target.value })}
                                        inputProps={{ 
                                            step: '0.01', 
                                            min: '0',
                                            max: dividaSelecionada.valor_parcela || dividaSelecionada.valor_total
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Observações"
                                        value={baixaFormData.observacoes}
                                        onChange={(e) => setBaixaFormData({ ...baixaFormData, observacoes: e.target.value })}
                                        multiline
                                        rows={3}
                                    />
                                </Grid>
                            </Grid>
                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBaixaDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleBaixaDivida} variant="contained" sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}>
                        Confirmar Pagamento
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default FluxoCaixa;

